import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'next-i18next'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import PEMDASActivityWrapper from '~/bootcamp/Activities/PEMDAS/components/PEMDASActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import { generateEquationAndResult } from './helpers'
import { Ranges, TaskHistory } from './types'

const MIN_NUMBER = 1
const MAX_NUMBER = 10

const ADDITION_COUNT = [1, 0, 1, 1]
const SUBTRACTION_COUNT = [0, 1, 1, 1]
const MULTIPLICATION_COUNT = [1, 1, 1, 2]

const MAX_HISTORY_LENGTH = [4, 4, 6, 9]

function MultiAddition({
  answerState,
  difficultyLevel,
  onNextQuestionClick,
  onAnswerStateChange,
  onInstanceChange,
  actionButtonsFC,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const [answer, setAnswer] = useState('')
  const [inputKey, setInputKey] = useState<string>(uuid())
  const { t } = useTranslation('generics')
  const { minNumber, maxNumber, additionCount, subtractionCount, multiplicationCount } = useMemo(
    () => getRanges(difficultyLevel),
    [difficultyLevel],
  )
  const [taskHistory, setTaskHistory] = useState<TaskHistory[]>([])
  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)
  const [{ expression, result }, setExpressionData] = useState({
    expression: '',
    result: 0,
  })

  useEffect(() => {
    const taskHistoryEquation = taskHistory.map((elem) => elem.equation)
    const equationAndResult = generateEquationAndResult(
      minNumber,
      maxNumber,
      taskHistoryEquation,
      additionCount,
      subtractionCount,
      multiplicationCount,
    )
    setAnswer('')
    setExpressionData({
      expression: equationAndResult.equation,
      result: equationAndResult.result,
    })
  }, [maxNumber, minNumber, additionCount, subtractionCount, multiplicationCount, taskHistory])

  useEffect(() => {
    onAnswerStateChange(null)
  }, [answer, onAnswerStateChange])

  useEmitInstance(onInstanceChange, expression ? `${expression.replaceAll('×', '\\cdot')} = \\square` : null)

  const handleOnCheckAnswerClick = (): void => {
    const isCorrect = Number(answer) === result
    onAnswerStateChange(isCorrect)
  }

  const handleOnNextQuestionClick = (): void => {
    setTaskHistory((prevState) => {
      return [{ equation: expression }, ...prevState].slice(0, MAX_HISTORY_LENGTH[difficultyLevel - 1])
    })
    onAnswerStateChange(null)
    onNextQuestionClick()
    setInputKey(uuid())
  }
  const onEnterPress = (): void => {
    if (answer !== '') {
      hocOnEnterPress(answerState, handleOnCheckAnswerClick, handleOnNextQuestionClick)
    }
  }

  return (
    <ActivityWrapper>
      <PEMDASActivityWrapper>
        <span className="tracking-widest">{expression}</span>
        <span className="block sm:inline-block">
          <Equals />
          <Input
            aria-label={t('ariaLabelButtons.insertResult')}
            autoFocus={autofocus}
            isError={answerState === false}
            key={inputKey}
            maxDigits={4}
            onChange={(value) => setAnswer(value)}
            onEnter={onEnterPress}
            ref={inputRef}
            value={answer}
          />
        </span>
      </PEMDASActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid: answer !== '',
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleOnCheckAnswerClick,
          clearFormAction: () => setAnswer(''),
          goToNextQuestionAction: handleOnNextQuestionClick,
        })}
    </ActivityWrapper>
  )
}

function getRanges(difficultyLevel: number | undefined): Ranges {
  const _difficultyLevel = difficultyLevel || 1
  return {
    minNumber: MIN_NUMBER,
    maxNumber: MAX_NUMBER,
    additionCount: ADDITION_COUNT[_difficultyLevel - 1],
    subtractionCount: SUBTRACTION_COUNT[_difficultyLevel - 1],
    multiplicationCount: MULTIPLICATION_COUNT[_difficultyLevel - 1],
  }
}

export default MultiAddition
